/* How a lineage answer reads. Pure functions, tested without a DOM.
 *
 * The screen's hardest job is not drawing a graph — it is refusing to let an incomplete answer look
 * complete. `docs/design/LINEAGE.md` names that as the failure mode an impact query has: a blast
 * radius listing twelve datasets when the real answer is forty, on the strength of which somebody
 * drops a column. Every function here exists because the obvious rendering gets that wrong.
 *
 * Three decisions are worth stating up front.
 *
 * **`completeness` returns a discriminated union, not a boolean and a string.** A caller cannot render
 * the "restricted" copy while holding a truncated answer, because the two states carry different
 * fields. That is the same structural move as `Coverage` on a `Score`: the guarantee travels with the
 * type rather than with a convention the next screen forgets.
 *
 * **A missing `withheld` is never read as zero.** The server omits the count for a caller without the
 * tenant-wide scope, and `undefined` there means "not disclosed", not "none". Treating it as zero
 * would turn a restricted answer into a complete-looking one — precisely the disclosure the D15
 * amendment gated, inverted into a lie.
 *
 * **Depth is rendered as rings.** "What breaks *first*" is the question during an incident, and depth
 * is the only proxy the graph carries for it. A flat list throws away the one ordering that matters.
 */

import type { AlertTone } from '@/shared/ui/Alert';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

import type {
  AffectedNode,
  Edge,
  EdgeSource,
  ImpactAnswer,
  IngestOperation,
  Node,
  NodeReach,
} from '../api/lineage';

/** Why an answer is not the whole picture, in a form a caller cannot mix up.
 *
 * `restricted` carries `withheldCount` — `number | null`, where `null` means the server declined to
 * say how many. That distinction survives into the type because "40 affected, you may see 8" and
 * "some are hidden from you" are different sentences, and only one of them is available to a caller
 * without the scope.
 */
export type Completeness =
  | { readonly kind: 'complete' }
  | { readonly kind: 'restricted'; readonly withheldCount: number | null }
  | { readonly kind: 'truncated' };

export function completeness(answer: ImpactAnswer): Completeness {
  if (answer.complete) return { kind: 'complete' };
  if (answer.reason === 'restricted') {
    /* `withheld` is optional on the wire. `?? null` rather than `?? 0`: the absence means the server
     * would not disclose the number, and a zero here would render "nothing was withheld" over an
     * answer the server has just told us is restricted. */
    return { kind: 'restricted', withheldCount: answer.withheld ?? null };
  }
  return { kind: 'truncated' };
}

/** The i18n key for an answer's completeness banner. One key per state, so no template branches. */
export function completenessKey(state: Completeness): string {
  switch (state.kind) {
    case 'complete':
      return 'lineage.completeness.complete';
    case 'truncated':
      return 'lineage.completeness.truncated';
    case 'restricted':
      return state.withheldCount === null
        ? 'lineage.completeness.restrictedUncounted'
        : 'lineage.completeness.restrictedCounted';
  }
}

/** `notice` for a complete answer, `warning` otherwise.
 *
 * Never `error`. An incomplete answer is not a failure — the request succeeded and the graph is what
 * it is — and colouring it red trains people to dismiss it, which is exactly how a partial blast
 * radius ends up acted on as if it were whole. `Alert` derives `role="alert"` from the `error` tone,
 * so choosing it here would also make a screen reader interrupt for a correct answer.
 */
export function completenessTone(state: Completeness): AlertTone {
  return state.kind === 'complete' ? 'notice' : 'warning';
}

export interface Ring {
  readonly depth: number;
  readonly nodes: readonly AffectedNode[];
}

/** Affected nodes grouped by hop distance, nearest first.
 *
 * Gaps are preserved rather than collapsed: if something is reachable at depth 1 and depth 4 with
 * nothing between, the rings are 1 and 4. Renumbering them 1 and 2 would tell a reader the far thing
 * is two hops away, which is a claim about the graph that the graph does not make.
 */
export function rings(answer: ImpactAnswer): readonly Ring[] {
  const byDepth = new Map<number, AffectedNode[]>();
  for (const node of answer.affected) {
    const bucket = byDepth.get(node.depth);
    if (bucket === undefined) byDepth.set(node.depth, [node]);
    else bucket.push(node);
  }
  return [...byDepth.entries()]
    .sort(([left], [right]) => left - right)
    .map(([depth, nodes]) => ({ depth, nodes }));
}

/** Whether any visible path ran through an inferred edge.
 *
 * Surfaced because a blast radius assembled partly from a model's guesses is a different artefact
 * from one a producer declared, and the source is the only thing that says which.
 */
export function anyInferred(answer: ImpactAnswer): boolean {
  return answer.affected.some((node) => node.through_inferred);
}

/** How much authority an edge's source carries, as an i18n key.
 *
 * A `sql_parse` edge is fact *subject to the parser being right about the dialect*, and that caveat
 * is the reason it is not merged with a facet. Rendering both as "declared" would erase the only
 * distinction an auditor asking "which edges did a model invent" can use.
 */
export function sourceKey(source: EdgeSource): string {
  return `lineage.source.${source}`;
}

/** What a run id starts with when this platform harvested the assertion itself.
 *
 * **The one string in this feature that is defined on both sides of the wire**, because there is no
 * field that carries the distinction: an edge read out of Snowflake's or Unity Catalog's own lineage
 * catalogue is stored as `openlineage_facet`, which is the right standing and the wrong provenance.
 * `core.lineage.application.native_harvest.RUN_ID_PREFIX` mints `native-<connection>-<digest>` for
 * exactly this reason, and its own docstring says so.
 *
 * Two definitions of one string is the drift this codebase records at five sites, so it is pinned
 * from the backend rather than trusted: `test_native_run_prefix_matches_the_client` reads this file
 * and fails if the two disagree. That is the same direction as the generated-client drift test — the
 * Python side checks the TypeScript — and it is what makes a rename safe rather than silent.
 */
export const RUN_ID_NATIVE_PREFIX = 'native-';

/** Confidence as a percentage, or `null` where the concept does not apply.
 *
 * The screen must render nothing rather than `0%` or `100%` for a non-inferred source. A facet carries
 * no confidence because the producing job simply stated the flow, and printing a number there would
 * invite a reader to treat a plain statement as a probability.
 *
 * **Three wire states, two meanings.** The generated type is `number | null | undefined`: the field is
 * optional *and* nullable, because the contract marks it non-required with a nullable type. `undefined`
 * means the server omitted it and `null` means it explicitly has none — and for this API those are the
 * same fact, so both collapse here. Written out rather than with `== null` so the collapse is a visible
 * decision instead of a loose comparison somebody later "tightens" into a bug.
 */
export function confidencePercent(edge: Edge): number | null {
  const confidence = edge.confidence;
  if (confidence === null || confidence === undefined) return null;
  return Math.round(confidence * 100);
}

/** Whether this edge is waiting on a person. Derived from state, never from confidence alone.
 *
 * An `llm_inference` edge *above* the floor is `active` and traversable while still being a guess, so
 * "has a confidence" and "needs review" are different questions and only the state answers the second.
 */
export function needsReview(edge: Edge): boolean {
  return edge.state === 'needs_review';
}

/** The i18n key describing what an ingest did. `partial` gets its own, and that is the point.
 *
 * A partial ingest updated the graph *and* refused something, so it is neither a success to move on
 * from nor a failure to retry. Collapsing it into either neighbour is how a knowingly incomplete
 * graph starts looking finished.
 */
export function ingestStateKey(operation: IngestOperation): string {
  return `lineage.ingest.state.${operation.state}`;
}

export function ingestTone(operation: IngestOperation): BadgeTone {
  switch (operation.state) {
    case 'succeeded':
      return 'ok';
    case 'partial':
      /* `warn`, not `ok`. A partial ingest wrote graph and refused something, so a green badge would
       * tell a reader the graph is current when impact queries past the refused edges under-report. */
      return 'warn';
    case 'failed':
      return 'danger';
    default:
      return 'info';
  }
}

/** An ISO instant for a date input, or `''` for "now".
 *
 * `datetime-local` wants no zone suffix, so the `Z` is dropped for display and re-added on the way
 * back — see `asOfFromInput`. Losing the round trip would silently reinterpret every instant in the
 * reader's local zone, which for a temporal graph turns a correct answer into a subtly wrong one.
 */
export function asOfToInput(asOf: string | null): string {
  return asOf === null ? '' : asOf.replace(/Z$/, '').slice(0, 16);
}

/** A `datetime-local` value back to an instant, or `null` for "now".
 *
 * The `:00Z` suffix is explicit rather than left to `new Date(...).toISOString()`, which would apply
 * the browser's zone offset. The server refuses a naive timestamp outright, so a value that lost its
 * zone here would be a 422 rather than a wrong answer — but a value that silently *gained* the
 * reader's offset would be neither, and that is the failure worth preventing.
 */
export function asOfFromInput(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return `${trimmed.length === 16 ? trimmed : trimmed.slice(0, 16)}:00Z`;
}

/** An ISO instant trimmed to the minute, for display beside an answer.
 *
 * The server echoes full microsecond precision — `2026-08-12T13:23:18.585509+00:00` — because that is
 * the instant it actually read the graph at, and rounding it server-side would make the answer and
 * the timestamp describe different moments. Trimming is therefore a *presentation* decision and
 * belongs here.
 *
 * The zone suffix is kept. An impact answer without one is ambiguous by exactly the offset of
 * whoever reads it, which for a temporal graph is the difference between a correct answer and a
 * plausible wrong one.
 */
export function formatInstant(instant: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(.*)$/.exec(
    instant,
  );
  if (match === null) return instant;

  // A capture group is `string | undefined` even when the pattern makes it mandatory, so the
  // absence is handled rather than asserted away. If either half is missing the input goes back
  // unchanged: a garbled timestamp is the server's problem to report, and half of one reformatted
  // to look valid is worse than the original.
  const date = match[1];
  const time = match[2];
  if (date === undefined || time === undefined) return instant;

  // Always separated by a space, `Z` and a numeric offset alike. The first version wrote
  // `13:23 UTC` but `13:23+00:00`, which is two formats for one field.
  const zone = match[3] ?? '';
  if (zone === '') return `${date} ${time}`;
  return `${date} ${time} ${zone === 'Z' ? 'UTC' : zone}`;
}

/* ---------------------------------------------------------------------------------------------
 * The picker: how much lineage a dataset has, and the four ways that question can be answered.
 * ------------------------------------------------------------------------------------------- */

/** What the index puts beside one dataset.
 *
 * **Four states, because there are four different facts** — the same discipline
 * `catalog-browser/model/grouping.ts` applies to a connection's name, and for the same reason: a
 * screen holding a dataset id and no count is about to make a claim, and the claim has to match the
 * evidence.
 *
 * * `counted` — the graph answered. Both figures are measurements, including a zero.
 * * `none` — the graph answered and this dataset is in it nowhere, or is in it connected to nothing.
 *   A statement about the *estate*: no producer has ever mentioned this table in a run event, which
 *   is the ordinary condition of most of a fresh install rather than a fault.
 * * `loading` — the reach read is in flight. Says nothing about the dataset.
 * * `unreadable` — that read failed. A fact about **us**, and the rows around it are still correct.
 *
 * Collapsing `unreadable` into `none` is the one that would do harm: it would report a request
 * failure as a property of a customer's data, which is the class this file's own `driftVerdict`
 * sibling records shipping three times on other screens.
 */
export type LineageSummary =
  | { readonly kind: 'counted'; readonly upstream: number; readonly downstream: number }
  | { readonly kind: 'none' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'unreadable' };

/** The reach read, as the picker sees it.
 *
 * `status` travels beside the map rather than being inferred from emptiness, because an empty map
 * means "loading", "failed" or "this tenant has no lineage at all" and those are three answers.
 */
export interface ReachLookup {
  readonly status: 'loading' | 'unreadable' | 'ready';
  readonly byDataset: ReadonlyMap<string, NodeReach>;
}

/** Reach keyed by *dataset* id rather than node id.
 *
 * Two routes have to be joined here and neither can do it alone: `/node-reach` is keyed on the node,
 * and only `/nodes` knows which dataset a node stands for. A node with `dataset_id: null` is dropped
 * — a job, or a dataset an OpenLineage producer named before any harvest saw the table — because the
 * picker's rows are catalogue datasets and there is nothing to key such a node against.
 *
 * `kind === 'dataset'` is checked as well, because `getNodeByDataset` filters on it server-side: a
 * column-level node carrying a dataset id would otherwise contribute its own reach to the table's
 * row, and the two are different subjects.
 */
export function reachByDataset(
  nodes: readonly Node[],
  reaches: readonly NodeReach[],
): ReadonlyMap<string, NodeReach> {
  const byNode = new Map(reaches.map((reach) => [reach.node_id, reach]));
  const byDataset = new Map<string, NodeReach>();
  for (const node of nodes) {
    /* `=== undefined` as well as `=== null`: the contract types `dataset_id` as nullable *and*
       optional, so a node whose payload omits it is a third shape and would reach `Map.set` with an
       undefined key. */
    if (node.dataset_id === null || node.dataset_id === undefined) continue;
    if (node.kind !== 'dataset') continue;
    const reach = byNode.get(node.id);
    if (reach !== undefined) byDataset.set(node.dataset_id, reach);
  }
  return byDataset;
}

/** Which of the four sentences one dataset gets. Ordered most-specific first. */
export function summaryFor(datasetId: string, lookup: ReachLookup): LineageSummary {
  if (lookup.status === 'loading') return { kind: 'loading' };
  if (lookup.status === 'unreadable') return { kind: 'unreadable' };
  const reach = lookup.byDataset.get(datasetId);
  /* Absent from the map and present-but-isolated are the same sentence deliberately: both mean no
     producer has connected this table to anything, and a reader has no use for the distinction
     between "no node" and "a node with no edges". The *route* keeps them apart — every node gets an
     explicit entry — so the collapse is this screen's decision rather than a gap in the data. */
  if (reach === undefined) return { kind: 'none' };
  if (reach.upstream === 0 && reach.downstream === 0) return { kind: 'none' };
  return { kind: 'counted', upstream: reach.upstream, downstream: reach.downstream };
}

/** The copy key for a summary that is not a pair of numbers.
 *
 * Its own `row.` namespace, because `lineage.pick.loading` is already the label on the screen-level
 * loading state and one key meaning both "the page is loading" and "this row's count is loading" is
 * how a sentence written for one surface appears on the other.
 */
export function summaryKey(summary: LineageSummary): string {
  return `lineage.pick.row.${summary.kind}`;
}
